import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import UserAvatar from "../components/UserAvatar";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import DOMPurify from "dompurify";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  RefreshCw,
  Users,
  Search,
  X,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Star,
  Award,
  Sparkles,
  Clock,
  Plus,
  Minus,
  Building2,
  Wallet,
} from "lucide-react";
import Select from "react-select";
import api from "../services/api";
import recognitionTypesService from "../services/recognitionTypesService";
import teamsService from "../services/teamsService";
import workvivoService from "../services/workvivoService";
import workvivoInboundKudosService from "../services/workvivoInboundKudosService";
import AuthContext from "../context/AuthContext";
import { getAvatarStyle } from "../utils/avatarColors";

// Helper to extract hex color from stored format (e.g., "from-[#a855f7]" -> "#a855f7")
const extractHexColor = (colorString) => {
  if (!colorString) return null;
  const match = colorString.match(/\[(.*?)\]/);
  return match ? match[1] : null;
};

// Format a USD amount for the points -> dollars conversion hint.
const formatUSD = (amount) => {
  const value = Number(amount) || 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
};

// Default fallback recognition types (used when API returns empty)
const DEFAULT_RECOGNITION_TYPES = [
  { value: "Kudos", label: "Kudos", icon: "👏", colorFrom: "#a855f7", colorTo: "#6366f1" },
  { value: "Thank You", label: "Thank You", icon: "🙏", colorFrom: "#22c55e", colorTo: "#10b981" },
  { value: "Great Work", label: "Great Work", icon: "⭐", colorFrom: "#eab308", colorTo: "#f97316" },
  { value: "Team Player", label: "Team Player", icon: "🤝", colorFrom: "#3b82f6", colorTo: "#06b6d4" },
  { value: "Innovation", label: "Innovation", icon: "💡", colorFrom: "#ec4899", colorTo: "#a855f7" },
  { value: "Leadership", label: "Leadership", icon: "🚀", colorFrom: "#f97316", colorTo: "#ef4444" },
];

const RecognitionPage = () => {
  const { user } = useContext(AuthContext);

  const [searchParams] = useSearchParams();
  const kudoSuggestionId = searchParams.get("workvivoKudoSuggestionId");
  const prefillRecipientId = searchParams.get("recipientId");
  const prefillTypeId = searchParams.get("typeId");
  const prefillOccasion = searchParams.get("occasion");
  const prefillEventName = searchParams.get("eventName");
  const navigate = useNavigate();
  const { data: kudoSuggestion } = useQuery({
    queryKey: ["workvivo-inbound-kudo", kudoSuggestionId],
    queryFn: () => workvivoInboundKudosService.getSuggestion(kudoSuggestionId),
    enabled: !!kudoSuggestionId,
  });
  const kudoAlreadyPosted =
    !!kudoSuggestion && ["converted", "deleted"].includes(kudoSuggestion.status);

  const [isTeamAdmin, setIsTeamAdmin] = useState(false);
  const canSeeBudget = user?.is_superuser || user?.role === "admin" || isTeamAdmin;
  const [recentRecognitions, setRecentRecognitions] = useState([]);
  const [isLoadingRecognitions, setIsLoadingRecognitions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Recognition types state
  const [recognitionTypes, setRecognitionTypes] = useState(DEFAULT_RECOGNITION_TYPES);
  // eslint-disable-next-line no-unused-vars
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);

  // Points settings state
  const [pointsSettings, setPointsSettings] = useState({ min_points: 10, max_points: 100, step: 10, allow_global_point_posting: false, points_to_dollar_rate: 1.0 });

  // Tab state: "teams" or "all"
  const [activeTab, setActiveTab] = useState("teams");

  // Teams-first state
  const [myTeamBudgets, setMyTeamBudgets] = useState([]); // from /teams/my-budgets
  const [expandedTeamId, setExpandedTeamId] = useState(null);
  const [teamMembers, setTeamMembers] = useState({}); // { teamId: [member objects] }
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [budgetError, setBudgetError] = useState(null);

  // All org members state
  const [allUsers, setAllUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // Form state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [recognitionType, setRecognitionType] = useState(DEFAULT_RECOGNITION_TYPES[0]);
  const [message, setMessage] = useState("");

  const isOccasionLocked = !!(
    prefillOccasion &&
    prefillRecipientId &&
    recognitionType?.isPrivate
  );
  const lockedRecipient = isOccasionLocked ? selectedUsers[0] : null;

  const handleCancelOccasion = () => {
    setSelectedUsers([]);
    setSelectedTeam(null);
    const firstVisible = recognitionTypes.find((t) => !t.isPrivate);
    if (firstVisible) setRecognitionType(firstVisible);
    navigate("/recognition", { replace: true });
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Placeholder.configure({ placeholder: "Share why you're recognizing this person..." }),
    ],
    content: "",
    editorProps: {
      attributes: {
        spellcheck: "true",
      },
    },
    onUpdate: ({ editor }) => {
      setMessage(editor.getHTML());
    },
  });
  const [points, setPoints] = useState(50);
  const [pointsHidden, setPointsHidden] = useState(true);
  // Draft string for the typed points field. Kept separate from `points` so a
  // half-typed value ("" or "5" on the way to "50") isn't clamped mid-keystroke.
  const [pointsInput, setPointsInput] = useState("50");

  // Prefill the form from a Workvivo inbound kudo once the suggestion loads.
  // Mirrors the "All Members" selection path: single recipient, no team, 0 points.
  useEffect(() => {
    if (!kudoSuggestion) return;
    const recipient = kudoSuggestion.recipient;
    // Only preselect recipients that exist in-app; unlinked Workvivo users
    // have no id and can't be submitted.
    if (recipient?.id) {
      setSelectedUsers([recipient]);
      setSelectedTeam(null);
      setPoints(0);
      setBudgetError(null);
      setActiveTab("all");
    }
    if (kudoSuggestion.message) {
      setMessage(kudoSuggestion.message);
      editor?.commands.setContent(kudoSuggestion.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kudoSuggestion, editor]);

  useEffect(() => {
    if (!prefillRecipientId) return;
    let cancelled = false;
    (async () => {
      try {
        const limit = 100;
        let skip = 0;
        let found = null;
        let totalCount = 0;
        let fetchedCount = 0;
        do {
          const response = await api.get("/api/v1/users/colleagues", {
            params: { skip, limit },
          });
          let usersData = [];
          if (Array.isArray(response.data)) {
            usersData = response.data;
            totalCount = usersData.length;
          } else if (response.data?.data && Array.isArray(response.data.data)) {
            usersData = response.data.data;
            totalCount = response.data.count || usersData.length;
          }
          fetchedCount += usersData.length;
          found = usersData.find((u) => (u.user_id || u.id) === prefillRecipientId);
          skip += limit;
        } while (!found && fetchedCount < totalCount);
        if (found && !cancelled) {
          setSelectedUsers([found]);
          setSelectedTeam(null);
          setPoints(0);
          setBudgetError(null);
          setActiveTab("all");
        }
      } catch (error) {
        console.error("Failed to prefill recipient:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillRecipientId]);

  // Visibility state. Team-scoped visibility always uses the team the
  // recipient was picked from, so there is no separate team picker.
  const [visibility, setVisibility] = useState("org");

  // Recipients chosen from "All Members" have no originating team, so team
  // visibility isn't available — fall back to org-wide.
  useEffect(() => {
    if (visibility === "team" && !selectedTeam) setVisibility("org");
  }, [visibility, selectedTeam]);

  // Workvivo cross-post state
  const [wvIntegration, setWvIntegration] = useState(null);
  const [postToWorkvivo, setPostToWorkvivo] = useState(false);
  const [wvAudienceMode, setWvAudienceMode] = useState("default");
  const [wvPostableSpaces, setWvPostableSpaces] = useState([]);
  const [wvSelectedSpaces, setWvSelectedSpaces] = useState([]);
  const [wvSpacesLoading, setWvSpacesLoading] = useState(false);
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const [colorsReady, setColorsReady] = useState(false);

  // Clamp points to valid range when settings change
  useEffect(() => {
    const minPts = pointsSettings.min_points ?? 10;
    const maxPts = pointsSettings.max_points || 100;
    if (points < minPts) {
      setPoints(minPts);
    } else if (points > maxPts) {
      setPoints(maxPts);
    }
  }, [pointsSettings.min_points, pointsSettings.max_points, points]);

  // Derived: selected team's budget info
  const selectedTeamBudget = selectedTeam
    ? myTeamBudgets.find((b) => b.team_id === selectedTeam.team_id)
    : null;
  const availableBudget = selectedTeamBudget?.available_amount
    ? parseFloat(selectedTeamBudget.available_amount)
    : 0;
  const hasBudget = selectedTeamBudget?.budget_id != null;

  // Calculate max points based on budget
  const maxPointsFromBudget = hasBudget ? Math.floor(availableBudget) : 0;
  const effectiveMaxPoints = hasBudget
    ? Math.min(pointsSettings.max_points || 100, maxPointsFromBudget)
    : 0;

  // Only show points section if at least one user selected from a team WITH a budget
  const showPointsSection = selectedUsers.length > 0 && selectedTeam && hasBudget;

  // Typed points entry. Accepts any whole number in range (not just multiples of
  // the step) — the step only drives the slider and the +/- buttons.
  const pointsMin = pointsSettings.min_points ?? 10;

  // Dollars per point, for the conversion hint under the points control.
  const pointsRate = pointsSettings.points_to_dollar_rate ?? 1.0;

  // Mirror committed points back into the field when it changes elsewhere
  // (slider, +/- buttons, picking a team, submitting).
  useEffect(() => {
    setPointsInput(String(points ?? 0));
  }, [points]);

  const handlePointsInputChange = (raw) => {
    const digits = raw.replace(/\D/g, "");
    setPointsInput(digits);
    const val = parseInt(digits, 10);
    if (!isNaN(val) && val >= pointsMin && val <= effectiveMaxPoints) {
      setPoints(val);
    }
  };

  // Snap whatever is in the field to the allowed range once the user is done.
  const handlePointsInputBlur = () => {
    const val = parseInt(pointsInput, 10);
    const clamped = isNaN(val)
      ? pointsMin
      : Math.max(pointsMin, Math.min(effectiveMaxPoints, val));
    setPoints(clamped);
    setPointsInput(String(clamped));
  };

  // Validate points against budget when team or points change (total cost = points * num recipients)
  useEffect(() => {
    const totalCost = points * Math.max(1, selectedUsers.length);
    if (selectedTeam && hasBudget && totalCost > availableBudget) {
      setBudgetError(`Insufficient budget. Need ${totalCost} points for ${selectedUsers.length} recipient${selectedUsers.length !== 1 ? 's' : ''}, available: ${Math.floor(availableBudget)}`);
    } else {
      setBudgetError(null);
    }
  }, [selectedTeam, points, availableBudget, hasBudget, selectedUsers.length]);

  const showNotification = (msg, type = "success") => {
    setNotification({ message: msg, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Fetch user's teams with budget info (team-first approach)
  const fetchMyTeams = async () => {
    setIsLoadingTeams(true);
    try {
      const budgetsData = await teamsService.getMyTeamBudgets();
      setMyTeamBudgets(budgetsData || []);

      // Check if user is team_admin in any of their teams
      let foundAdmin = false;
      for (const tb of budgetsData || []) {
        try {
          const members = await teamsService.getTeamMembers(tb.team_id);
          const myMembership = (members || []).find((m) => m.user_id === user?.id);
          if (myMembership?.role === "team_admin") {
            foundAdmin = true;
            break;
          }
        } catch (err) {
          // ignore per-team errors
        }
      }
      setIsTeamAdmin(foundAdmin);
    } catch (error) {
      console.error("Failed to load teams:", error);
      setMyTeamBudgets([]);
    } finally {
      setIsLoadingTeams(false);
    }
  };

  // Fetch members when a team is expanded
  const fetchTeamMembers = async (teamId) => {
    if (teamMembers[teamId]) return; // Already loaded
    setIsLoadingMembers(true);
    try {
      const members = await teamsService.getTeamMembers(teamId);
      // Filter out current user and inactive members
      const otherMembers = (members || []).filter(
        (m) => m.user_id !== user?.id && m.is_active !== false
      );
      setTeamMembers((prev) => ({ ...prev, [teamId]: otherMembers }));
    } catch (error) {
      console.error(`Failed to load members for team ${teamId}:`, error);
      setTeamMembers((prev) => ({ ...prev, [teamId]: [] }));
    } finally {
      setIsLoadingMembers(false);
    }
  };

  // Fetch all org members for "All Members" tab
  const fetchAllUsers = async () => {
    if (allUsers.length > 0) return; // Already loaded
    setIsLoadingUsers(true);
    try {
      const limit = 100;
      let skip = 0;
      let fetched = [];
      let totalCount = 0;
      do {
        const response = await api.get("/api/v1/users/colleagues", {
          params: { skip, limit },
        });
        let usersData = [];
        let responseCount = 0;
        if (Array.isArray(response.data)) {
          usersData = response.data;
          responseCount = response.data.length;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          usersData = response.data.data;
          responseCount = response.data.count || response.data.data.length;
        }
        fetched = [...fetched, ...usersData];
        totalCount = responseCount;
        skip += limit;
      } while (fetched.length < totalCount);
      setAllUsers(fetched.filter((u) => u.id !== user?.id));
    } catch (error) {
      console.error("Failed to load users:", error);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Handle tab switch
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setSearchTerm("");
    setSelectedUsers([]);
    setSelectedTeam(null);
    if (tab === "all") {
      fetchAllUsers();
    }
  };

  // Handle selecting a user from the "All Members" tab (no team, no points)
  const handleAllMemberSelect = (member) => {
    const memberId = member.user_id || member.id;
    setSelectedUsers(prev => {
      const already = prev.some(u => (u.user_id || u.id) === memberId);
      return already ? prev.filter(u => (u.user_id || u.id) !== memberId) : [...prev, member];
    });
    setSelectedTeam(null);
    setPoints(0);
    setBudgetError(null);
  };

  // Handle team expand/collapse
  const handleTeamClick = (teamBudget) => {
    const teamId = teamBudget.team_id;
    if (expandedTeamId === teamId) {
      setExpandedTeamId(null);
      return;
    }
    // Clear selection when switching to a different team
    if (expandedTeamId && expandedTeamId !== teamId) {
      setSelectedUsers([]);
    }
    setExpandedTeamId(teamId);
    setSelectedTeam(teamBudget);
    fetchTeamMembers(teamId);
  };

  // Handle member selection from expanded team (toggle)
  const handleMemberSelect = (member, teamBudget) => {
    const memberId = member.user_id || member.id;
    setSelectedUsers(prev => {
      const already = prev.some(u => (u.user_id || u.id) === memberId);
      return already ? prev.filter(u => (u.user_id || u.id) !== memberId) : [...prev, member];
    });
    setSelectedTeam(teamBudget);
    setBudgetError(null);
    // Set points to middle of range or max available, whichever is lower
    const available = parseFloat(teamBudget.available_amount || 0);
    const teamHasBudget = teamBudget.budget_id != null;
    if (teamHasBudget && available > 0) {
      const step = pointsSettings.step || 10;
      const midPoint = Math.round((pointsSettings.min_points + pointsSettings.max_points) / 2 / step) * step;
      const maxAllowed = Math.min(midPoint, Math.floor(available));
      const roundedPoints = Math.floor(maxAllowed / step) * step;
      setPoints(Math.max(pointsSettings.min_points, roundedPoints));
    } else {
      setPoints(0);
    }
  };

  // Select (or clear) every member of the expanded team in one click.
  const handleRecognizeEntireTeam = (members, teamBudget) => {
    const eligible = members.filter((m) => (m.user_id || m.id) !== user?.id);
    if (eligible.length === 0) return;
    const allSelected = eligible.every((m) =>
      selectedUsers.some((u) => (u.user_id || u.id) === (m.user_id || m.id))
    );
    if (allSelected) {
      // Toggle off when the whole team is already selected.
      const eligibleIds = new Set(eligible.map((m) => m.user_id || m.id));
      setSelectedUsers((prev) =>
        prev.filter((u) => !eligibleIds.has(u.user_id || u.id))
      );
      return;
    }
    setSelectedUsers(eligible);
    setSelectedTeam(teamBudget);
    setBudgetError(null);
    const available = parseFloat(teamBudget.available_amount || 0);
    const teamHasBudget = teamBudget.budget_id != null;
    if (teamHasBudget && available > 0) {
      const step = pointsSettings.step || 10;
      const minPts = pointsSettings.min_points ?? 10;
      const maxPts = pointsSettings.max_points || 100;
      const perPersonFromBudget = Math.floor(available / eligible.length);
      const capped = Math.min(maxPts, perPersonFromBudget);
      const stepAligned = Math.floor(capped / step) * step;
      setPoints(Math.max(minPts, stepAligned));
    } else {
      setPoints(0);
    }
  };

  // Fetch recent recognitions
  const fetchRecentRecognitions = async () => {
    setIsLoadingRecognitions(true);
    try {
      const response = await api.get("/api/v1/recognitions/feed", {
        params: { skip: 0, limit: 4 },
      });
      const allRecognitions = Array.isArray(response.data) ? response.data : [];
      const sorted = [...allRecognitions].sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
      );
      setRecentRecognitions(sorted);
    } catch (error) {
      console.error("Failed to load recognitions:", error);
    } finally {
      setIsLoadingRecognitions(false);
    }
  };

  // Submit recognition
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (kudoAlreadyPosted) {
      showNotification("This Workvivo shoutout has already been posted.", "error");
      return;
    }

    if (selectedUsers.length === 0) {
      showNotification("Please select at least one person to recognize", "error");
      return;
    }

    if (!message || message === "<p></p>" || message === "<p><br></p>") {
      showNotification("Please enter a recognition message", "error");
      return;
    }

    // Check if team has a budget allocated (only if team is selected)
    if (selectedTeam && points > 0 && !hasBudget) {
      showNotification("Selected team has no budget allocated. Contact your admin.", "error");
      return;
    }

    // Check if total points exceed available budget
    const totalCost = points * selectedUsers.length;
    if (selectedTeam && points > 0 && totalCost > availableBudget) {
      showNotification(`Insufficient budget. Need ${totalCost} points for ${selectedUsers.length} recipients, available: ${Math.floor(availableBudget)} points`, "error");
      return;
    }

    // Determine final points amount (only give points if team is selected)
    const finalPoints = selectedTeam ? points : 0;

    // Resolve Workvivo space ids from audience mode
    let workvivoSpaceIds = null;
    if (postToWorkvivo) {
      if (wvAudienceMode === "global") {
        workvivoSpaceIds = [];
      } else if (wvAudienceMode === "default") {
        workvivoSpaceIds = wvIntegration?.default_space_external_id
          ? [wvIntegration.default_space_external_id]
          : [];
      } else if (wvAudienceMode === "custom") {
        workvivoSpaceIds = wvSelectedSpaces.map((s) => s.value);
      }
    }

    setIsSubmitting(true);
    try {
      const response = await api.post("/api/v1/recognitions/", {
        recipient_ids: isOccasionLocked
          ? [prefillRecipientId]
          : selectedUsers.map(u => u.user_id || u.id),
        message: message,
        points_amount: finalPoints,
        recognition_type_id: recognitionType.id || null,
        team_id: selectedTeam?.team_id || null,
        visibility,
        visible_team_id: visibility === "team" ? selectedTeam?.team_id : null,
        occasion_label:
          recognitionType.isPrivate && prefillOccasion === "event" && prefillEventName
            ? prefillEventName
            : null,
        post_to_workvivo: selectedUsers.length === 1 ? !!postToWorkvivo : false,
        workvivo_space_external_ids: selectedUsers.length === 1 ? workvivoSpaceIds : null,
        points_hidden: finalPoints > 0 ? pointsHidden : false,
        workvivo_kudo_suggestion_id: kudoSuggestionId || null,
      });

      // Surface Workvivo failure without blocking the recognition.
      if (
        postToWorkvivo &&
        response.data?.workvivo &&
        response.data.workvivo.status !== "posted"
      ) {
        const detail = response.data.workvivo.last_error || "Unknown error";
        showNotification(
          `Recognition sent but Workvivo posting failed: ${detail}. Please report to AddFlint. You can retry from the feed.`,
          "error"
        );
      }

      const recipientNames = selectedUsers.map(u => u.full_name || u.email).join(", ");
      if (finalPoints > 0) {
        showNotification(
          `Recognition sent to ${recipientNames}! Points are pending team admin approval.`
        );
      } else {
        showNotification(`Recognition sent to ${recipientNames}!`);
      }

      // Reset form
      setSelectedUsers([]);
      setSelectedTeam(null);
      setExpandedTeamId(null);
      setMessage("");
      editor?.commands.clearContent();
      // Reset points to middle of range, rounded to nearest step
      const step = pointsSettings.step || 10;
      const midPoint = Math.round((pointsSettings.min_points + pointsSettings.max_points) / 2 / step) * step;
      setPoints(midPoint);
      setPointsHidden(true);
      setRecognitionType(recognitionTypes[0]);
      setVisibility("org");
      setPostToWorkvivo(false);
      setWvAudienceMode("default");
      setWvSelectedSpaces([]);
      if (isOccasionLocked) navigate("/recognition", { replace: true });

      // Refresh recognitions and team budgets
      fetchRecentRecognitions();
      fetchMyTeams(); // Refresh budgets to show updated amounts
    } catch (error) {
      // Handle validation errors (array of {type, loc, msg, input})
      let errorMsg = "Failed to send recognition";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMsg = detail.map(e => e.msg || e.message || String(e)).join(", ");
        } else if (typeof detail === "string") {
          errorMsg = detail;
        } else if (typeof detail === "object" && detail.msg) {
          errorMsg = detail.msg;
        }
      }
      showNotification(errorMsg, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch recognition types from API
  const fetchRecognitionTypes = async () => {
    setIsLoadingTypes(true);
    try {
      const apiTypes = await recognitionTypesService.getMyRecognitionTypes(!!prefillTypeId);
      if (apiTypes && apiTypes.length > 0) {
        // Transform API data to component format with hex colors
        const transformedTypes = apiTypes.map((t) => ({
          id: t.id, // Keep the actual ID for API submission
          value: t.value,
          label: t.name,
          icon: t.icon,
          description: t.description,
          colorFrom: extractHexColor(t.color_from) || "#a855f7",
          colorTo: extractHexColor(t.color_to) || "#3b82f6",
          isPrivate: !!t.is_private,
        }));
        const visibleTypes = transformedTypes.filter((t) => !t.isPrivate);
        setRecognitionTypes(visibleTypes);
        const prefillType = prefillTypeId
          ? transformedTypes.find((t) => t.id === prefillTypeId)
          : null;
        if (prefillType) {
          setRecognitionType(prefillType);
        } else if (!visibleTypes.find((t) => t.value === recognitionType.value)) {
          // Set first type as selected if current selection is not in new types
          setRecognitionType(visibleTypes[0]);
        }
      }
    } catch (error) {
      console.error("Failed to load recognition types:", error);
    } finally {
      setIsLoadingTypes(false);
      setColorsReady(true);
    }
  };

  // Fetch points settings from API
  const fetchPointsSettings = async () => {
    try {
      const settings = await recognitionTypesService.getPointsSettings();
      console.log("[RecognitionPage] Points settings from API:", settings);
      // Ensure we have valid numbers with fallbacks
      const minPts = settings.min_points ?? 10;
      const maxPts = settings.max_points ?? 100;
      const stepVal = settings.step ?? 10;
      console.log("[RecognitionPage] Using points settings:", { minPts, maxPts, stepVal });

      setPointsSettings({
        min_points: minPts,
        max_points: maxPts,
        step: stepVal,
        allow_global_point_posting: settings.allow_global_point_posting ?? false,
        points_to_dollar_rate: settings.points_to_dollar_rate ?? 1.0,
      });

      // Set initial points to middle of range, rounded to nearest step, clamped to valid range
      const midPoint = Math.round((minPts + maxPts) / 2 / stepVal) * stepVal;
      const clampedPoints = Math.max(minPts, Math.min(maxPts, midPoint));
      setPoints(clampedPoints);
    } catch (error) {
      console.error("Failed to load points settings:", error);
      // Keep using defaults on error
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchMyTeams();
    fetchRecentRecognitions();
    fetchRecognitionTypes();
    fetchPointsSettings();
    // Workvivo: check if org has integration
    workvivoService
      .getRecognitionIntegrationStatus()
      .then((status) => setWvIntegration(status))
      .catch(() => setWvIntegration({ enabled: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Fetch Workvivo postable spaces when recipient changes (single recipient only)
  useEffect(() => {
    const singleRecipient = selectedUsers.length === 1 ? selectedUsers[0] : null;
    const recipientId = singleRecipient?.user_id || singleRecipient?.id;
    if (!postToWorkvivo || !recipientId) {
      setWvPostableSpaces([]);
      return;
    }
    setWvSpacesLoading(true);
    workvivoService
      .getPostableSpaces(recipientId)
      .then((spaces) => setWvPostableSpaces(spaces || []))
      .catch(() => setWvPostableSpaces([]))
      .finally(() => setWvSpacesLoading(false));
  }, [postToWorkvivo, selectedUsers]);

  const ct = '0.5s ease';
  const displayColorFrom = colorsReady ? recognitionType.colorFrom : 'transparent';
  const displayColorTo   = colorsReady ? recognitionType.colorTo   : 'transparent';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 pt-3 sm:pt-8">
        {/* Notification */}
        <AnimatePresence>
          {notification && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`fixed top-20 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-2xl shadow-lg ${
                notification.type === "error"
                  ? "bg-red-500 text-white"
                  : "bg-green-500 text-white"
              }`}
            >
              {notification.type === "error" ? (
                <AlertCircle className="w-5 h-5" />
              ) : (
                <CheckCircle2 className="w-5 h-5" />
              )}
              {notification.message}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recognition Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-2xl border border-gray-200 p-6 transition-colors duration-100 hover:border-black/30">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <Sparkles className="w-5 h-5" style={{ color: displayColorFrom, transition: `color ${ct}` }} />
                Send Recognition
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Tab Selector */}
                <div>
                  {/* Occasion sends are locked to the linked person */}
                  {isOccasionLocked && (
                    <div
                      className="flex items-center gap-3 p-4 rounded-2xl"
                      style={{
                        background: `${displayColorFrom}12`,
                        border: `1px solid ${displayColorFrom}44`,
                      }}
                    >
                      {lockedRecipient ? (
                        <>
                          {lockedRecipient.avatar_url ? (
                            <img
                              src={lockedRecipient.avatar_url}
                              alt={lockedRecipient.full_name || lockedRecipient.email}
                              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                              style={{ border: `2px solid ${displayColorFrom}` }}
                            />
                          ) : (
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                              style={{
                                ...getAvatarStyle(lockedRecipient.full_name || lockedRecipient.email || lockedRecipient.id),
                                border: `2px solid ${displayColorFrom}`,
                              }}
                            >
                              {(lockedRecipient.full_name || lockedRecipient.email || "?")[0].toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-gray-900 truncate">
                              {lockedRecipient.full_name || lockedRecipient.email}
                            </div>
                            <div className="text-xs text-gray-500">
                              This {prefillOccasion === "anniversary" ? "work anniversary" : "birthday"} recognition is just for them.
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-gray-500 flex-1">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading recipient...
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={handleCancelOccasion}
                        className="text-xs text-gray-500 hover:text-gray-700 underline flex-shrink-0"
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                  {!isOccasionLocked && (
                  <>
                  <div className="flex border-b border-gray-200 mb-4">
                    <button
                      type="button"
                      onClick={() => handleTabSwitch("teams")}
                      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === "teams"
                          ? ""
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                      style={activeTab === "teams" && colorsReady ? { borderColor: displayColorFrom, color: displayColorFrom, transition: `color ${ct}, border-color ${ct}` } : {}}
                    >
                      <Building2 className="w-4 h-4" />
                      My Teams
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTabSwitch("all")}
                      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === "all"
                          ? ""
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                      style={activeTab === "all" && colorsReady ? { borderColor: displayColorFrom, color: displayColorFrom, transition: `color ${ct}, border-color ${ct}` } : {}}
                    >
                      <Users className="w-4 h-4" />
                      All Members
                    </button>
                  </div>

                  {/* All Members Tab */}
                  {activeTab === "all" && (
                    <div>
                      {/* Search */}
                      <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search by name, email, or department..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                        {searchTerm && (
                          <button
                            type="button"
                            onClick={() => setSearchTerm("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-2xl p-2">
                        {isLoadingUsers ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
                          </div>
                        ) : (() => {
                          const filtered = allUsers.filter((u) => {
                            if (!searchTerm) return true;
                            const term = searchTerm.toLowerCase();
                            return (
                              u.full_name?.toLowerCase().includes(term) ||
                              u.email?.toLowerCase().includes(term) ||
                              u.department?.toLowerCase().includes(term)
                            );
                          });
                          return filtered.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 text-sm">
                              {searchTerm ? "No users match your search" : "No users available"}
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                              {filtered.map((u) => {
                                const isSelected = selectedUsers.some(s => (s.user_id || s.id) === (u.user_id || u.id));
                                return (
                                  <button
                                    key={u.id}
                                    type="button"
                                    onClick={() => handleAllMemberSelect(u)}
                                    className="flex items-center gap-2 p-2 rounded-2xl text-left border-2"
                                    style={isSelected ? { background: `${displayColorFrom}18`, borderColor: displayColorFrom, transition: `background ${ct}, border-color ${ct}` } : { borderColor: 'transparent', transition: `background ${ct}, border-color ${ct}` }}
                                  >
                                    <UserAvatar src={u.avatar_url} name={u.full_name || u.email} className="w-8 h-8 rounded-full object-cover flex-shrink-0" textClassName="text-xs" />
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm font-medium text-gray-900 truncate">{u.full_name || "—"}</div>
                                      <div className="text-xs text-gray-500 truncate">{u.department || u.email}</div>
                                    </div>
                                    {isSelected && <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: displayColorFrom, transition: `color ${ct}` }} />}
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* My Teams Tab */}
                  {activeTab === "teams" && isLoadingTeams ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
                    </div>
                  ) : activeTab === "teams" && myTeamBudgets.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-2xl border border-gray-200">
                      <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm">You are not a member of any teams yet.</p>
                      <p className="text-xs text-gray-400 mt-1">Contact your admin to be added to a team.</p>
                    </div>
                  ) : activeTab === "teams" && (
                    <div className="space-y-3">
                      {/* Search members within expanded team */}
                      {expandedTeamId && (
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search team members..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                          {searchTerm && (
                            <button
                              type="button"
                              onClick={() => setSearchTerm("")}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )}

                      {myTeamBudgets.map((tb) => {
                        const isExpanded = expandedTeamId === tb.team_id;
                        const members = teamMembers[tb.team_id] || [];
                        const tbAvailable = parseFloat(tb.available_amount || 0);
                        const tbTotal = parseFloat(tb.total_amount || 0);
                        const tbHasBudget = tb.budget_id != null;
                        const filteredMembers = isExpanded && searchTerm
                          ? members.filter((m) =>
                              (m.full_name || m.email || "").toLowerCase().includes(searchTerm.toLowerCase())
                            )
                          : members;

                        return (
                          <div key={tb.team_id} className="border border-gray-200 rounded-2xl overflow-hidden">
                            {/* Team Header (clickable) */}
                            <button
                              type="button"
                              onClick={() => handleTeamClick(tb)}
                              className={`w-full flex items-center justify-between gap-2 p-3 sm:p-4 text-left ${isExpanded ? '' : 'hover:bg-gray-50'}`}
                              style={isExpanded ? { background: `${displayColorFrom}18`, borderBottom: `1px solid ${displayColorFrom}33`, transition: `background ${ct}` } : { transition: `background ${ct}` }}
                            >
                              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={isExpanded ? { background: `${displayColorFrom}25`, transition: `background ${ct}` } : { background: '#f3f4f6', transition: `background ${ct}` }}>
                                  <Users className="w-4 h-4 sm:w-5 sm:h-5" style={isExpanded ? { color: displayColorFrom, transition: `color ${ct}` } : { color: '#6b7280', transition: `color ${ct}` }} />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium text-gray-900 text-sm sm:text-base truncate">{tb.team_name}</div>
                                  {canSeeBudget && (
                                    <div className="text-[10px] sm:text-xs text-gray-500 truncate">
                                      {tbHasBudget
                                        ? `${Math.floor(tbAvailable)} / ${Math.floor(tbTotal)} pts available`
                                        : "No budget allocated"}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                                {canSeeBudget && tbHasBudget && (
                                  <span className="inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap" style={colorsReady ? { background: `${displayColorFrom}20`, color: displayColorFrom, transition: `background ${ct}, color ${ct}` } : { background: '#f3f4f6', color: '#6b7280' }}>
                                    <Wallet className="w-3 h-3 mr-1" />
                                    {Math.floor(tbAvailable)} pts
                                  </span>
                                )}
                                <svg
                                  className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </button>

                            {/* Expanded: Team Members */}
                            <AnimatePresence>
                              {isExpanded && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="overflow-hidden"
                                >
                                  <div className="p-1.5 sm:p-3 max-h-64 overflow-y-auto">
                                    {!isLoadingMembers && members.length > 0 && (() => {
                                      const eligibleMembers = members.filter((m) => (m.user_id || m.id) !== user?.id);
                                      if (eligibleMembers.length === 0) return null;
                                      const allSelected = eligibleMembers.every((m) =>
                                        selectedUsers.some((u) => (u.user_id || u.id) === (m.user_id || m.id))
                                      );
                                      return (
                                        <button
                                          type="button"
                                          onClick={() => handleRecognizeEntireTeam(members, tb)}
                                          className="w-full mb-3 flex items-center justify-center gap-1.5 sm:gap-2 px-3 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium border-2 whitespace-nowrap transition-all"
                                          style={allSelected
                                            ? { background: displayColorFrom, borderColor: displayColorFrom, color: 'white' }
                                            : { background: `${displayColorFrom}10`, borderColor: `${displayColorFrom}66`, color: displayColorFrom }}
                                        >
                                          <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                                          <span className="truncate">
                                            {allSelected
                                              ? `Clear entire team (${eligibleMembers.length})`
                                              : `Recognize entire team (${eligibleMembers.length})`}
                                          </span>
                                        </button>
                                      );
                                    })()}
                                    {isLoadingMembers && !members.length ? (
                                      <div className="flex items-center justify-center py-4">
                                        <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                                      </div>
                                    ) : filteredMembers.length === 0 ? (
                                      <div className="text-center py-4 text-sm text-gray-500">
                                        {searchTerm ? "No members match your search" : "No other members in this team"}
                                      </div>
                                    ) : (
                                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                        {filteredMembers.map((m) => {
                                          const memberId = m.user_id || m.id;
                                          const isSelected = selectedUsers.some(u => (u.user_id || u.id) === memberId);
                                          return (
                                            <button
                                              key={memberId}
                                              type="button"
                                              onClick={() => handleMemberSelect(m, tb)}
                                              className="flex items-center gap-2 p-2 rounded-2xl text-left border-2"
                                              style={isSelected ? { background: `${displayColorFrom}18`, borderColor: displayColorFrom, transition: `background ${ct}, border-color ${ct}` } : { borderColor: 'transparent', transition: `background ${ct}, border-color ${ct}` }}
                                            >
                                              <UserAvatar
                                                src={m.avatar_url}
                                                name={m.full_name || m.email}
                                                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                                textClassName="text-xs"
                                              />
                                              <div className="min-w-0 flex-1">
                                                <div className="text-sm font-medium text-gray-900 truncate">
                                                  {m.full_name || "—"}
                                                </div>
                                                <div className="text-xs text-gray-500 truncate">
                                                  {m.role || m.email}
                                                </div>
                                              </div>
                                              {isSelected && (
                                                <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: displayColorFrom, transition: `color ${ct}` }} />
                                              )}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  </>
                  )}

                  {/* Selected Users Summary */}
                  {!isOccasionLocked && selectedUsers.length > 0 && (
                    <div className="mt-3 p-3 rounded-2xl" style={{ background: `${displayColorFrom}15`, border: `1px solid ${displayColorFrom}33`, transition: `background ${ct}, border-color ${ct}` }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Recognizing {selectedUsers.length} {selectedUsers.length === 1 ? "person" : "people"}
                          {selectedTeam && canSeeBudget && hasBudget && (
                            <span className="font-normal text-gray-500 ml-1">— {Math.floor(availableBudget)} pts available</span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={() => { setSelectedUsers([]); setSelectedTeam(null); }}
                          className="text-gray-400 hover:text-gray-600 text-xs"
                        >
                          Clear all
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selectedUsers.map((u) => {
                          const uid = u.user_id || u.id;
                          return (
                            <div key={uid} className="flex items-center gap-1.5 bg-white rounded-full px-2 py-1 shadow-sm border" style={{ borderColor: `${displayColorFrom}44` }}>
                              <UserAvatar src={u.avatar_url} name={u.full_name || u.email} className="w-5 h-5 rounded-full object-cover" textClassName="text-xs" />
                              <span className="text-xs font-medium text-gray-800">{u.full_name || u.email}</span>
                              <button
                                type="button"
                                onClick={() => setSelectedUsers(prev => prev.filter(s => (s.user_id || s.id) !== uid))}
                                className="text-gray-400 hover:text-gray-600 ml-0.5"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Recognition Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recognition Type
                  </label>
                  {recognitionType.isPrivate && (
                    <div
                      className="mb-3 flex items-center gap-2 p-3 rounded-2xl text-sm"
                      style={{
                        background: `${recognitionType.colorFrom}15`,
                        border: `1px solid ${recognitionType.colorFrom}44`,
                      }}
                    >
                      <span className="text-xl">{recognitionType.icon || "🎉"}</span>
                      <span className="text-gray-700">
                        {prefillOccasion === "event" && prefillEventName ? (
                          <>
                            Sending a recognition for{" "}
                            <span className="font-semibold" style={{ color: recognitionType.colorFrom }}>
                              {prefillEventName}
                            </span>{" "}
                            — pick who to recognize!
                          </>
                        ) : (
                          <>
                            Sending a <span className="font-semibold" style={{ color: recognitionType.colorFrom }}>{recognitionType.label}</span> recognition
                            {prefillOccasion === "birthday"
                              ? " — wish them a happy birthday!"
                              : prefillOccasion === "anniversary"
                                ? " — celebrate their milestone!"
                                : ""}
                          </>
                        )}
                      </span>
                    </div>
                  )}
                  {!isOccasionLocked && (!colorsReady ? (
                    /* Skeleton pills — shown while recognition types are loading */
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 sm:gap-2">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="p-2 sm:p-3 rounded-2xl bg-gray-100 animate-pulse h-[56px] sm:h-[72px]" />
                      ))}
                    </div>
                  ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 sm:gap-2">
                    {recognitionTypes.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setRecognitionType(type)}
                        className={`p-2 sm:p-3 rounded-2xl text-center transition-all duration-100 ${
                          recognitionType.value === type.value
                            ? "text-white shadow-md scale-[1.02]"
                            : "text-gray-700 hover:brightness-110 hover:shadow-md hover:-translate-y-0.5"
                        }`}
                        style={
                          recognitionType.value === type.value
                            ? { background: `linear-gradient(to right, ${type.colorFrom}, ${type.colorTo})` }
                            : { background: `linear-gradient(to right, ${type.colorFrom}22, ${type.colorTo}18)` }
                        }
                      >
                        <div className="text-lg sm:text-2xl mb-0.5 sm:mb-1">{type.icon && type.icon.startsWith('/api/v1/recognition-type-icons/') ? (
                          <img src={type.icon} alt="" className="w-6 h-6 sm:w-8 sm:h-8 object-contain mx-auto" />
                        ) : (type.icon || '🏅')}</div>
                        <div className="text-[10px] sm:text-xs font-medium leading-tight">
                          {type.label}
                        </div>
                      </button>
                    ))}
                  </div>
                  ))}

                  {/* Selected type description */}
                  <div
                    className={`grid transition-all duration-300 ease-out ${
                      recognitionType?.description
                        ? "grid-rows-[1fr] opacity-100 mt-2"
                        : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="text-sm text-gray-500">{recognitionType?.description}</p>
                    </div>
                  </div>
                </div>

                {/* Points - only show when a team with budget is selected */}
                {showPointsSection && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Points to Award{selectedUsers.length > 1 ? " (per person)" : ""}
                    {canSeeBudget && (
                      <span className="text-gray-500 font-normal ml-1">
                        ({pointsSettings.min_points} - {effectiveMaxPoints} available
                        {selectedUsers.length > 1 && `, total: ${points * selectedUsers.length}`})
                      </span>
                    )}
                  </label>

                  {/* Budget Error Message */}
                  {budgetError && (
                    <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-2xl flex items-center gap-2 text-sm text-red-700">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      {budgetError}
                    </div>
                  )}

                  {effectiveMaxPoints >= (pointsSettings.min_points ?? 10) ? (
                    <div className="flex items-center gap-3">
                      {/* Minus Button */}
                      <button
                        type="button"
                        onClick={() => {
                          const newVal = points - (pointsSettings.step || 10);
                          setPoints(Math.max(pointsSettings.min_points ?? 10, newVal));
                        }}
                        disabled={points <= (pointsSettings.min_points ?? 10)}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Minus className="w-5 h-5" />
                      </button>

                      {/* Slider */}
                      <input
                        type="range"
                        min={pointsSettings.min_points ?? 10}
                        max={effectiveMaxPoints}
                        step={pointsSettings.step || 10}
                        value={Math.min(points, effectiveMaxPoints) || (pointsSettings.min_points ?? 10)}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          if (!isNaN(val)) {
                            const clamped = Math.max(
                              pointsSettings.min_points ?? 10,
                              Math.min(effectiveMaxPoints, val)
                            );
                            setPoints(clamped);
                          }
                        }}
                        className="flex-1 h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-purple-600"
                      />

                      {/* Plus Button */}
                      <button
                        type="button"
                        onClick={() => {
                          const newVal = points + (pointsSettings.step || 10);
                          setPoints(Math.min(effectiveMaxPoints, newVal));
                        }}
                        disabled={points >= effectiveMaxPoints}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Plus className="w-5 h-5" />
                      </button>

                      {/* Points Display — type an exact amount */}
                      <label className={`flex items-center gap-1 pl-3 pr-1 py-2 rounded-full font-bold min-w-[80px] justify-center cursor-text focus-within:ring-2 focus-within:ring-purple-400 focus-within:ring-offset-1 ${
                        budgetError
                          ? 'bg-red-500 text-white'
                          : 'recognition-gradient text-white'
                      }`}>
                        <Star className="w-4 h-4 flex-shrink-0" />
                        <input
                          type="text"
                          inputMode="numeric"
                          aria-label={`Points to award, ${pointsMin} to ${effectiveMaxPoints}`}
                          value={pointsInput}
                          onChange={(e) => handlePointsInputChange(e.target.value)}
                          onBlur={handlePointsInputBlur}
                          onFocus={(e) => e.target.select()}
                          onKeyDown={(e) => {
                            // Enter would otherwise submit the recognition form.
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.currentTarget.blur();
                            }
                          }}
                          className="w-12 bg-transparent text-white font-bold text-center outline-none border-0 p-0"
                        />
                      </label>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                        <span className="text-sm text-gray-500">Insufficient team budget</span>
                      </div>
                      <div className="flex items-center gap-1 bg-gray-300 text-gray-600 px-4 py-2 rounded-full font-bold min-w-[80px] justify-center">
                        <Star className="w-4 h-4" />
                        0
                      </div>
                    </div>
                  )}

                  {/* Points -> dollars conversion */}
                  <p className="text-xs text-gray-500 mt-2">
                    1 point = {formatUSD(pointsRate)}
                    {points > 0 && (
                      <>
                        {" · "}
                        <span className="font-medium text-gray-700">
                          {points} {points === 1 ? "point" : "points"} = {formatUSD(points * pointsRate)}
                        </span>
                        {selectedUsers.length > 1 &&
                          ` · ${selectedUsers.length} recipients = ${formatUSD(points * pointsRate * selectedUsers.length)}`}
                      </>
                    )}
                  </p>

                  {points > 0 && pointsSettings.allow_global_point_posting && (
                    <label className="flex items-center gap-2 mt-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!pointsHidden}
                        onChange={(e) => setPointsHidden(!e.target.checked)}
                        className="h-4 w-4"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Make points public
                      </span>
                    </label>
                  )}
                </div>
                )}

                {/* Message */}
                <div>
                  <div
                    className="rounded-2xl overflow-hidden border-2"
                    style={{
                      borderColor: isEditorFocused ? displayColorFrom : `${displayColorFrom}44`,
                      boxShadow: isEditorFocused ? `0 0 0 3px ${displayColorFrom}33, 0 2px 8px rgba(0,0,0,0.08)` : '0 1px 3px rgba(0,0,0,0.06)',
                      transition: `border-color ${ct}, box-shadow ${ct}`,
                    }}
                    onFocus={() => setIsEditorFocused(true)}
                    onBlur={() => setIsEditorFocused(false)}
                  >
                    {/* Header bar — mirrors feed card style */}
                    <div
                      className="relative overflow-hidden px-3 py-2 flex items-center gap-2"
                      style={{ background: 'linear-gradient(to right, #9ca3af, #6b7280)' }}
                    >
                      <AnimatePresence>
                        {colorsReady && (
                          <motion.div
                            key={recognitionType.value}
                            className="absolute inset-0"
                            style={{ background: `linear-gradient(to right, ${displayColorFrom}, ${displayColorTo})` }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5 }}
                          />
                        )}
                      </AnimatePresence>
                      <Sparkles className="relative w-4 h-4 text-white" />
                      <span className="relative text-white text-sm font-medium">Recognition Message</span>
                    </div>
                    {/* Toolbar */}
                    <div className="flex items-center gap-1 px-2 py-1 border-b bg-white" style={{ borderColor: `${displayColorFrom}22`, transition: `border-color ${ct}` }}>
                      <button type="button" onClick={() => editor?.chain().focus().toggleBold().run()}
                        className={`px-2 py-1 rounded text-sm font-bold ${editor?.isActive("bold") ? "bg-gray-200" : "hover:bg-gray-100"}`}>B</button>
                      <button type="button" onClick={() => editor?.chain().focus().toggleItalic().run()}
                        className={`px-2 py-1 rounded text-sm italic ${editor?.isActive("italic") ? "bg-gray-200" : "hover:bg-gray-100"}`}>I</button>
                      <button type="button" onClick={() => editor?.chain().focus().toggleUnderline().run()}
                        className={`px-2 py-1 rounded text-sm underline ${editor?.isActive("underline") ? "bg-gray-200" : "hover:bg-gray-100"}`}>U</button>
                      <span className="w-px h-4 bg-gray-300 mx-1" />
                      <button type="button" onClick={() => editor?.chain().focus().toggleBulletList().run()}
                        className={`px-2 py-1 rounded text-sm ${editor?.isActive("bulletList") ? "bg-gray-200" : "hover:bg-gray-100"}`}>• List</button>
                      <button type="button" onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                        className={`px-2 py-1 rounded text-sm ${editor?.isActive("orderedList") ? "bg-gray-200" : "hover:bg-gray-100"}`}>1. List</button>
                    </div>
                    <EditorContent editor={editor} />
                  </div>
                </div>

                {/* Visibility */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Who can see this?</label>
                  <div className="flex gap-1.5 sm:gap-2">
                    <button type="button" onClick={() => setVisibility("org")}
                      className={`flex-1 px-2 sm:px-3 py-2 rounded-full border text-xs sm:text-sm whitespace-nowrap transition-all duration-100 ${visibility === "org" ? "bg-blue-500 text-white border-blue-500 shadow-md" : "bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:border-blue-300 hover:shadow-sm hover:-translate-y-0.5"}`}>
                      🌐 Everyone
                    </button>
                    <button type="button" onClick={() => setVisibility("team")} disabled={!selectedTeam}
                      title={!selectedTeam ? "Pick someone from a team to use team visibility" : undefined}
                      className={`flex-1 px-2 sm:px-3 py-2 rounded-full border text-xs sm:text-sm whitespace-nowrap transition-all duration-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:translate-y-0 disabled:hover:shadow-none ${visibility === "team" ? "bg-purple-500 text-white border-purple-500 shadow-md" : "bg-white text-gray-700 border-gray-300 hover:bg-purple-50 hover:border-purple-300 hover:shadow-sm hover:-translate-y-0.5"}`}>
                      👥 Team
                    </button>
                    <button type="button" onClick={() => setVisibility("private")}
                      className={`flex-1 px-2 sm:px-3 py-2 rounded-full border text-xs sm:text-sm whitespace-nowrap transition-all duration-100 ${visibility === "private" ? "bg-orange-600 text-white border-orange-600 shadow-md" : "bg-white text-gray-700 border-gray-300 hover:bg-orange-50 hover:border-orange-300 hover:shadow-sm hover:-translate-y-0.5"}`}>
                      🔒 Private
                    </button>
                  </div>
                  {visibility === "team" && selectedTeam && (
                    <p className="text-xs text-gray-500 mt-1">
                      Only members of <span className="font-medium text-gray-700">{selectedTeam.team_name}</span> will see this recognition.
                    </p>
                  )}
                  {visibility !== "team" && !selectedTeam && (
                    <p className="text-xs text-gray-400 mt-1">Team visibility is available when you pick a recipient from one of your teams.</p>
                  )}
                  {visibility === "private" && (
                    <p className="text-xs text-gray-500 mt-1">Only you and the recipient will see this recognition.</p>
                  )}
                </div>

                {/* Workvivo cross-post */}
                {wvIntegration?.enabled && kudoSuggestionId && (
                  <div className="border border-gray-200 rounded-2xl p-4 bg-gray-50">
                    <p className="text-sm text-gray-600">This recognition comes from a Workvivo shoutout and is already posted there, so it won't be posted again.</p>
                  </div>
                )}
                {wvIntegration?.enabled && !kudoSuggestionId && (
                  <div className="border border-gray-200 rounded-2xl p-4 bg-gray-50">
                    <label className={`flex items-center gap-2 ${selectedUsers.length > 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                      <input type="checkbox" checked={postToWorkvivo && selectedUsers.length <= 1} onChange={(e) => setPostToWorkvivo(e.target.checked)} className="h-4 w-4" disabled={selectedUsers.length > 1} />
                      <span className="text-sm font-medium text-gray-700">Also post as a kudo on Workvivo</span>
                    </label>
                    {selectedUsers.length > 1 && (
                      <p className="text-xs text-gray-500 mt-1">Workvivo posting is only available for single-recipient recognitions.</p>
                    )}
                    {postToWorkvivo && !wvIntegration.sender_linked && (
                      <p className="text-xs text-orange-700 mt-2">Your account is not linked to Workvivo — posting may fail.</p>
                    )}
                    {postToWorkvivo && (
                      <div className="mt-3 space-y-2">
                        <label className="block text-xs font-medium text-gray-700">Audience</label>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setWvAudienceMode("default")} disabled={!wvIntegration.default_space_external_id}
                            className={`flex-1 px-2 py-1.5 rounded-full border text-xs ${wvAudienceMode === "default" ? "bg-blue-500 text-white border-blue-500" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"} disabled:opacity-50 disabled:cursor-not-allowed`}>
                            ⭐ Default space
                          </button>
                          <button type="button" onClick={() => setWvAudienceMode("global")}
                            className={`flex-1 px-2 py-1.5 rounded-full border text-xs ${wvAudienceMode === "global" ? "bg-blue-500 text-white border-blue-500" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"}`}>
                            🌐 Global
                          </button>
                          <button type="button" onClick={() => setWvAudienceMode("custom")}
                            className={`flex-1 px-2 py-1.5 rounded-full border text-xs ${wvAudienceMode === "custom" ? "bg-blue-500 text-white border-blue-500" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"}`}>
                            🎯 Pick spaces
                          </button>
                        </div>
                        {wvAudienceMode === "custom" && (
                          <>
                            <Select
                              isMulti
                              isLoading={wvSpacesLoading}
                              options={wvPostableSpaces.map((s) => ({
                                value: s.external_space_id,
                                label: s.is_default ? `${s.name} (default)` : s.name,
                              }))}
                              value={wvSelectedSpaces}
                              onChange={(opts) => setWvSelectedSpaces(opts || [])}
                              placeholder={selectedUsers.length !== 1 ? "Pick a single recipient first..." : wvPostableSpaces.length === 0 ? "No shared spaces" : "Select spaces..."}
                              noOptionsMessage={() => "No spaces — you and the recipient share none"}
                              className="text-sm"
                            />
                            <p className="text-xs text-gray-500">Only spaces where both you and the recipient are members.</p>
                          </>
                        )}
                        {visibility !== "org" && (
                          <p className="text-xs text-orange-700">
                            This recognition is {visibility}-only in the app, but the Workvivo post will be visible to whoever can see the chosen audience.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {kudoAlreadyPosted && (
                  <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    This Workvivo shoutout has already been posted, so it can't be sent again.
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting || kudoAlreadyPosted || selectedUsers.length === 0 || !message || message === "<p></p>" || message === "<p><br></p>" || (points > 0 && budgetError)}
                  className="relative overflow-hidden w-full flex items-center justify-center gap-2 px-6 py-3 text-white rounded-full font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(to right, #9ca3af, #6b7280)' }}
                >
                  <AnimatePresence>
                    {colorsReady && (
                      <motion.span
                        key={recognitionType.value}
                        className="absolute inset-0"
                        style={{ background: `linear-gradient(to right, ${displayColorFrom}, ${displayColorTo})` }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                      />
                    )}
                  </AnimatePresence>
                  <span className="relative flex items-center gap-2">
                    {isSubmitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                    {isSubmitting
                      ? (showPointsSection && points > 0 ? "Awarding..." : "Sending...")
                      : (showPointsSection && points > 0 ? `Award ${points} Points` : "Send Recognition")}
                  </span>
                </button>
                {selectedUsers.length > 0 && !showPointsSection && (
                  <p className="text-center text-xs text-gray-500 mt-2">
                    No points will be awarded with this recognition (0 pts).
                  </p>
                )}
              </form>
            </div>
          </motion.div>

          {/* Recent Recognitions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-col h-full"
          >
            <div className="bg-white rounded-2xl border border-gray-200 p-6 transition-colors duration-100 hover:border-black/30">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-600" />
                  Recent Sent
                </h2>
                <button
                  onClick={fetchRecentRecognitions}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${
                      isLoadingRecognitions ? "animate-spin" : ""
                    }`}
                  />
                </button>
              </div>

              {isLoadingRecognitions ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
                </div>
              ) : recentRecognitions.length === 0 ? (
                <div className="text-center py-8">
                  <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">
                    No recognitions yet
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    Be the first to recognize a colleague!
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentRecognitions.slice(0, 3).map((recognition, index) => {
                    const typeFrom = recognition.recognition_type?.color_from
                      ? extractHexColor(recognition.recognition_type.color_from) || recognition.recognition_type.color_from
                      : '#a855f7';
                    const typeTo = recognition.recognition_type?.color_to
                      ? extractHexColor(recognition.recognition_type.color_to) || recognition.recognition_type.color_to
                      : '#6366f1';
                    return (
                      <div
                        key={recognition.id || index}
                        className="bg-white rounded-2xl shadow-sm overflow-hidden border-2 transition-all duration-200 hover:shadow-lg cursor-pointer"
                        style={{ borderColor: `${typeFrom}66` }}
                      >
                        {/* Type badge header */}
                        <div
                          className="px-3 py-1.5 flex items-center gap-2"
                          style={{ background: `linear-gradient(to right, ${typeFrom}, ${typeTo})` }}
                        >
                          <Award className="w-3.5 h-3.5 text-white" />
                          <span className="text-white text-xs font-medium">Recognition</span>
                          {recognition.recognition_type && (
                            <span className="flex items-center gap-1 pl-1.5 pr-2 py-0.5 rounded-full text-[10px] font-semibold ml-auto shadow-sm bg-white/95 text-gray-900">
                              {recognition.recognition_type.emoji && recognition.recognition_type.emoji.startsWith('/api/v1/recognition-type-icons/') ? (
                                <img src={recognition.recognition_type.emoji} alt="" className="w-3 h-3 object-contain" />
                              ) : (recognition.recognition_type.emoji || '🏅')}
                              {recognition.occasion_label || recognition.recognition_type.name}
                            </span>
                          )}
                        </div>

                        {/* Body */}
                        <div className="p-3">
                          <div className="flex items-start gap-2">
                            <UserAvatar
                              src={recognition.sender?.avatar_url}
                              name={recognition.sender?.full_name || recognition.sender?.email}
                              className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                              textClassName="text-xs"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900">
                                {recognition.sender?.full_name ||
                                  recognition.sender?.email ||
                                  "User"}
                                <span className="text-gray-400 font-normal mx-1">→</span>
                                <span style={{ color: typeFrom }}>
                                  {recognition.recipient?.full_name ||
                                    recognition.recipient?.email ||
                                    "User"}
                                </span>
                              </div>
                              <div className="text-xs text-gray-600 mt-1 line-clamp-2 prose prose-xs max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(recognition.message || "") }} />
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                {recognition.points_amount > 0 && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    <Star className="w-3 h-3 mr-1" />
                                    {recognition.points_amount} pts
                                  </span>
                                )}
                                <span className="text-xs text-gray-400">
                                  {recognition.timestamp
                                    ? new Date(recognition.timestamp).toLocaleDateString()
                                    : ""}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="mt-auto pt-6 bg-white rounded-2xl border border-gray-200 p-6 transition-colors duration-100 hover:border-black/30">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                Quick Stats
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-purple-50 rounded-2xl text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {recentRecognitions.length}
                  </div>
                  <div className="text-xs text-purple-700">Sent</div>
                </div>
                <div className="p-4 bg-blue-50 rounded-2xl text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {myTeamBudgets.length}
                  </div>
                  <div className="text-xs text-blue-700">Your Teams</div>
                </div>
              </div>

              {/* Teams Info */}
              {myTeamBudgets.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-purple-600" />
                    Your Teams ({myTeamBudgets.length})
                  </h3>
                  <div className="space-y-1">
                    {myTeamBudgets.slice(0, 3).map((tb) => (
                      <div
                        key={tb.team_id}
                        className="text-xs text-gray-600 px-2 py-1 bg-gray-50 rounded"
                      >
                        {tb.team_name}
                      </div>
                    ))}
                    {myTeamBudgets.length > 3 && (
                      <div className="text-xs text-gray-500">
                        +{myTeamBudgets.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              )}

              {myTeamBudgets.length === 0 && !isLoadingTeams && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="text-xs text-orange-600 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    You're not assigned to any teams
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Contact your admin to be added to a team to give points.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RecognitionPage;
